import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineHint } from "@/components/PageStates";
import { AUTH_REDIRECT_MESSAGE_KEY } from "@/lib/authSession";
import { getDefaultRouteForRole } from "@/lib/roles";
import { getFieldErrorClass, getFieldMessageClass, isValidEmail } from "@/lib/form-ui";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, LogIn, ShieldCheck, UserPlus } from "lucide-react";

const PasswordToggleButton = ({ visible, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`${visible ? "Sembunyikan" : "Tampilkan"} ${label}`}
    aria-pressed={visible}
    className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-slate-100 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
  >
    {visible ? <EyeOff size={16} /> : <Eye size={16} />}
  </button>
);

const LoginPage = () => {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [signupForm, setSignupForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingSignup, setLoadingSignup] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});
  const [authMessage, setAuthMessage] = useState("");
  const { login, signupAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const redirectMessage = sessionStorage.getItem(AUTH_REDIRECT_MESSAGE_KEY);
    if (!redirectMessage) {
      return;
    }

    setAuthMessage(redirectMessage);
    sessionStorage.removeItem(AUTH_REDIRECT_MESSAGE_KEY);
  }, []);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setLoginErrors({});
    setSignupErrors({});
    setAuthMessage("");
    setShowLoginPassword(false);
    setShowSignupPassword(false);
    setShowSignupConfirmPassword(false);
  };

  const validateLogin = () => {
    const nextErrors = {};
    if (!String(username || "").trim()) {
      nextErrors.username = "Username wajib diisi.";
    }
    if (!String(password || "").trim()) {
      nextErrors.password = "Password wajib diisi.";
    }
    return nextErrors;
  };

  const validateSignup = () => {
    const nextErrors = {};
    if (!String(signupForm.username || "").trim()) {
      nextErrors.username = "Username wajib diisi.";
    }
    if (!String(signupForm.email || "").trim()) {
      nextErrors.email = "Email wajib diisi.";
    } else if (!isValidEmail(signupForm.email)) {
      nextErrors.email = "Format email belum valid.";
    }
    if (!String(signupForm.password || "")) {
      nextErrors.password = "Password wajib diisi.";
    } else if (String(signupForm.password).length < 8) {
      nextErrors.password = "Password minimal 8 karakter.";
    }
    if (!String(signupForm.confirmPassword || "")) {
      nextErrors.confirmPassword = "Konfirmasi password wajib diisi.";
    } else if (signupForm.password !== signupForm.confirmPassword) {
      nextErrors.confirmPassword = "Konfirmasi password tidak sama.";
    }
    return nextErrors;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateLogin();
    setLoginErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setAuthMessage("");
    setLoadingLogin(true);
    try {
      const userData = await login(username, password);
      toast.success("Login berhasil!");
      navigate(getDefaultRouteForRole(userData?.role), { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Login gagal";
      setAuthMessage(message);
      toast.error(message);
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateSignup();
    setSignupErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setAuthMessage("");
    setLoadingSignup(true);
    try {
      await signupAdmin({
        username: signupForm.username,
        email: signupForm.email,
        password: signupForm.password,
      });
      toast.success("Pendaftaran berhasil. Menunggu persetujuan Kepala Ruangan.");
      setUsername(signupForm.username);
      setSignupForm({ username: "", email: "", password: "", confirmPassword: "" });
      handleModeChange("login");
    } catch (error) {
      const message = error.response?.data?.detail || "Pendaftaran gagal";
      setAuthMessage(message);
      toast.error(message);
    } finally {
      setLoadingSignup(false);
    }
  };

  return (
    <div className="relative isolate min-h-screen overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0">
        <div
          className="absolute inset-0 transform-gpu animate-login-pan bg-cover bg-center motion-reduce:animate-none"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/4947388/pexels-photo-4947388.jpeg')",
          }}
        >
          <div className="absolute inset-0 bg-slate-950/45" />
        </div>

        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl transform-gpu animate-login-float-slow motion-reduce:animate-none" />
        <div className="absolute right-[-6rem] top-1/3 h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl transform-gpu animate-login-float-fast motion-reduce:animate-none" />
        <div
          className="absolute bottom-[-8rem] h-72 w-72 rounded-full bg-white/10 blur-3xl transform-gpu animate-login-float-slow motion-reduce:animate-none"
          style={{ animationDelay: "4s", left: "calc(50% - 9rem)" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.16),_transparent_35%),linear-gradient(180deg,_rgba(2,6,23,0.05)_0%,_rgba(2,6,23,0.45)_100%)]" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="glass-panel relative overflow-hidden rounded-[28px] p-8 animate-login-rise motion-reduce:animate-none">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-300" />

            <div className="mb-8 flex flex-col items-center text-center">
              <div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-inner shadow-primary/10 ring-1 ring-primary/10 animate-login-rise motion-reduce:animate-none"
                style={{ animationDelay: "120ms" }}
              >
                <ShieldCheck size={28} />
              </div>
              <h1 className="mb-2 font-manrope text-3xl font-bold tracking-tight text-primary">
                Sistem Inventory RS Panglima Sebaya
              </h1>
              <p className="font-inter text-muted-foreground">
                Login untuk mengelola inventaris rumah sakit
              </p>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-2 rounded-2xl bg-muted p-1">
              <button
                type="button"
                data-testid="switch-login-mode"
                aria-pressed={mode === "login"}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  mode === "login"
                    ? "scale-[1.01] bg-background text-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleModeChange("login")}
              >
                Masuk
              </button>
              <button
                type="button"
                data-testid="switch-signup-mode"
                aria-pressed={mode === "signup"}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  mode === "signup"
                    ? "scale-[1.01] bg-background text-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleModeChange("signup")}
              >
                Daftar Admin
              </button>
            </div>

            {authMessage ? (
              <div
                className="mb-6 animate-login-rise motion-reduce:animate-none"
                style={{ animationDelay: "260ms" }}
              >
                <InlineHint className="border-red-200 bg-red-50/90 text-red-900">
                  {authMessage}
                </InlineHint>
              </div>
            ) : null}

            {mode === "login" ? (
              <form
                key="login"
                onSubmit={handleLoginSubmit}
                className="space-y-6 animate-login-rise motion-reduce:animate-none"
                style={{ animationDelay: "320ms" }}
              >
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setLoginErrors((prev) => ({ ...prev, username: "" }));
                      setAuthMessage("");
                    }}
                    placeholder="Masukkan username"
                    aria-invalid={Boolean(loginErrors.username)}
                    className={getFieldErrorClass(loginErrors.username)}
                    required
                  />
                  {loginErrors.username ? (
                    <p className={getFieldMessageClass(true)}>{loginErrors.username}</p>
                  ) : (
                    <p className={getFieldMessageClass()}>Gunakan username akun yang sudah terdaftar.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showLoginPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setLoginErrors((prev) => ({ ...prev, password: "" }));
                        setAuthMessage("");
                      }}
                      placeholder="Masukkan password"
                      aria-invalid={Boolean(loginErrors.password)}
                      className={[getFieldErrorClass(loginErrors.password), "pr-12"].filter(Boolean).join(" ")}
                      required
                    />
                    <PasswordToggleButton
                      visible={showLoginPassword}
                      label="password"
                      onClick={() => setShowLoginPassword((prev) => !prev)}
                    />
                  </div>
                  {loginErrors.password ? (
                    <p className={getFieldMessageClass(true)}>{loginErrors.password}</p>
                  ) : null}
                </div>

                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  className="w-full"
                  disabled={loadingLogin}
                >
                  {loadingLogin ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    <>
                      <LogIn size={16} className="mr-2" />
                      Masuk
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form
                key="signup"
                onSubmit={handleSignupSubmit}
                className="space-y-4 animate-login-rise motion-reduce:animate-none"
                style={{ animationDelay: "320ms" }}
              >
                <InlineHint>
                  Pendaftaran admin tetap memerlukan persetujuan Kepala Ruangan sebelum akun bisa dipakai masuk.
                </InlineHint>

                <div className="space-y-2">
                  <Label htmlFor="signup-username">Username</Label>
                  <Input
                    id="signup-username"
                    data-testid="signup-username-input"
                    type="text"
                    value={signupForm.username}
                    onChange={(e) => {
                      setSignupForm((prev) => ({ ...prev, username: e.target.value }));
                      setSignupErrors((prev) => ({ ...prev, username: "" }));
                      setAuthMessage("");
                    }}
                    placeholder="Buat username"
                    aria-invalid={Boolean(signupErrors.username)}
                    className={getFieldErrorClass(signupErrors.username)}
                    required
                  />
                  {signupErrors.username ? (
                    <p className={getFieldMessageClass(true)}>{signupErrors.username}</p>
                  ) : (
                    <p className={getFieldMessageClass()}>Gunakan nama akun yang mudah dikenali saat proses persetujuan.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    data-testid="signup-email-input"
                    type="email"
                    value={signupForm.email}
                    onChange={(e) => {
                      setSignupForm((prev) => ({ ...prev, email: e.target.value }));
                      setSignupErrors((prev) => ({ ...prev, email: "" }));
                      setAuthMessage("");
                    }}
                    placeholder="Masukkan email"
                    aria-invalid={Boolean(signupErrors.email)}
                    className={getFieldErrorClass(signupErrors.email)}
                    required
                  />
                  {signupErrors.email ? (
                    <p className={getFieldMessageClass(true)}>{signupErrors.email}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      data-testid="signup-password-input"
                      type={showSignupPassword ? "text" : "password"}
                      value={signupForm.password}
                      onChange={(e) => {
                        setSignupForm((prev) => ({ ...prev, password: e.target.value }));
                        setSignupErrors((prev) => ({ ...prev, password: "" }));
                        setAuthMessage("");
                      }}
                      placeholder="Buat password"
                      aria-invalid={Boolean(signupErrors.password)}
                      className={[getFieldErrorClass(signupErrors.password), "pr-12"].filter(Boolean).join(" ")}
                      required
                    />
                    <PasswordToggleButton
                      visible={showSignupPassword}
                      label="password admin"
                      onClick={() => setShowSignupPassword((prev) => !prev)}
                    />
                  </div>
                  {signupErrors.password ? (
                    <p className={getFieldMessageClass(true)}>{signupErrors.password}</p>
                  ) : (
                    <p className={getFieldMessageClass()}>Gunakan minimal 8 karakter agar akun lebih aman.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Konfirmasi Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      data-testid="signup-confirm-password-input"
                      type={showSignupConfirmPassword ? "text" : "password"}
                      value={signupForm.confirmPassword}
                      onChange={(e) => {
                        setSignupForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
                        setSignupErrors((prev) => ({ ...prev, confirmPassword: "" }));
                        setAuthMessage("");
                      }}
                      placeholder="Ulangi password"
                      aria-invalid={Boolean(signupErrors.confirmPassword)}
                      className={[getFieldErrorClass(signupErrors.confirmPassword), "pr-12"].filter(Boolean).join(" ")}
                      required
                    />
                    <PasswordToggleButton
                      visible={showSignupConfirmPassword}
                      label="konfirmasi password"
                      onClick={() => setShowSignupConfirmPassword((prev) => !prev)}
                    />
                  </div>
                  {signupErrors.confirmPassword ? (
                    <p className={getFieldMessageClass(true)}>{signupErrors.confirmPassword}</p>
                  ) : null}
                </div>

                <Button
                  data-testid="signup-submit-button"
                  type="submit"
                  className="w-full"
                  disabled={loadingSignup}
                >
                  {loadingSignup ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    <>
                      <UserPlus size={16} className="mr-2" />
                      Daftar Admin
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
